import { DISMISS, useNotification } from "collections";
import {
  useDeviceConfigQuery,
  useStartDeviceConfigExportQuery
} from "generated/graphql";
import { useEffect, useState } from "react";
import { useTranslation } from "utilities/i18n";

type DeviceConfigArgs = {
  deviceId: string;
  deviceModel: string;
  fetchDeviceConfig: boolean;
  tenantId: string;
  fetchInterval?: number;
};

type STATUS = "IDLE" | "FETCHING" | "DONE";
export const MAX_FETCH_COUNT = 4;
export const FETCH_INTERVAL = 3000;

export const useDeviceConfig = ({
  deviceId,
  deviceModel,
  fetchDeviceConfig,
  tenantId,
  fetchInterval = FETCH_INTERVAL
}: DeviceConfigArgs) => {
  const [addNotification] = useNotification();
  const { t } = useTranslation();
  const [status, setStatus] = useState<STATUS>("IDLE");
  const [fetchCount, setFetchCount] = useState(0);
  const [hasResult, setHasResult] = useState(false);
  const deviceConfigSupported = !!deviceId && fetchDeviceConfig;

  // start export device configuration
  const {
    data: startExportData,
    error: startExportError,
    fetching: startExportFetching
  } = useStartDeviceConfigExportQuery({
    variables: {
      deviceId,
      tenantId
    },
    pause: status !== "IDLE" || !deviceConfigSupported,
    requestPolicy: "network-only"
  });
  const hasExportError =
    startExportData?.startExportDeviceConfigs?.status === false ||
    !!startExportError;

  // set status to "FETCHING" if start export was successful
  useEffect(() => {
    if (startExportData?.startExportDeviceConfigs?.status === true) {
      setStatus("FETCHING");
    }
  }, [startExportData?.startExportDeviceConfigs?.status]);

  const {
    data: deviceConfigData,
    error: deviceConfigError,
    fetching: deviceConfigFetching,
    executor: getDeviceConfig
  } = useDeviceConfigQuery({
    variables: {
      deviceId,
      deviceModel,
      tenantId
    },
    pause: true
  });

  // query for the config response if start export was successfully executed (status === "FETCHING")
  // we should query a few times, before finally throwing error
  const shouldFetch =
    status === "FETCHING" &&
    fetchCount < MAX_FETCH_COUNT &&
    deviceConfigSupported;
  useEffect(() => {
    if (!shouldFetch || deviceConfigFetching) {
      return;
    }

    const timerId = setTimeout(() => {
      getDeviceConfig({ requestPolicy: "network-only" });
      setFetchCount(fetchCount + 1);
    }, fetchInterval);

    return () => clearTimeout(timerId);
  }, [
    shouldFetch,
    deviceConfigFetching,
    getDeviceConfig,
    fetchCount,
    fetchInterval
  ]);

  const lastAttemptFinished =
    fetchCount >= MAX_FETCH_COUNT && !deviceConfigFetching;

  const hasDeviceConfigError =
    lastAttemptFinished &&
    (deviceConfigData?.getExtendedConfigParams?.status === false ||
      !!deviceConfigError);

  // update state when first results come
  // once this happens Loading... text will be replaced by those results, and then automatically updated once there's more data
  useEffect(() => {
    if (
      !hasResult &&
      deviceConfigData?.getExtendedConfigParams?.status === true
    ) {
      setHasResult(true);
    }
  }, [deviceConfigData?.getExtendedConfigParams?.status, hasResult]);

  // set status to "DONE" once number of attempts is exhausted
  useEffect(() => {
    if (lastAttemptFinished) {
      setStatus("DONE");
    }
  }, [lastAttemptFinished]);

  // show error and set status to "DONE" if:
  // a) starting the export was unsuccessful, or
  // b) get configuration returned error even after few attempts
  useEffect(() => {
    if (hasExportError || hasDeviceConfigError) {
      addNotification({
        id: "device-config-error",
        type: "error",
        message: t("DEVICE_CONFIG_ERROR"),
        dismiss: DISMISS.DEFAULT
      });
      setStatus("DONE");
    }
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExportError, hasDeviceConfigError]);

  return {
    fetching: startExportFetching || (status === "FETCHING" && !hasResult),
    deviceConfig: deviceConfigData?.getExtendedConfigParams?.configParams ?? []
  };
};
